import getWindow from 'Utils/get-window';

/**
 * Page-side receiver for SELF-RENDERED GAM native ads — the browser half of
 * the native-creative handshake (same friendly-iframe + SafeFrame postMessage
 * pattern as flint-render.ts, but flowing the other direction: the creative
 * pushes the native field VALUES up to the page, and the page renders them
 * into its own React card instead of using GAM's native template).
 *
 * GAM serves native creatives to a `['fluid']` slot. A tiny GAM "native style"
 * (trafficked by adops on the infeed unit — NOT part of this build) runs inside
 * the slot's iframe and, on render, hands us the native fields for its slot's
 * div id. We stash them here and notify the React NativeAdCard registered for
 * that div id, which renders the promoted-post card. On no-fill (or a native
 * style that never calls us) the card renders nothing and the tile hides —
 * exactly like the display slot's empty-hide.
 *
 * Two callback paths, mirroring flint-render.ts / Prebid Universal Creative:
 *
 * 1. Friendly iframe (GAM default for custom native styles): the creative
 *    calls window.parent.__flipboardRenderNativeAd(divId, fields).
 * 2. SafeFrame (cross-origin — `parent` is unreachable): the creative posts
 *    {type: 'native-ad-fields', divId, fields} and the page reads it off the
 *    message. (Unlike flint-render, there is no reply/markup step: the native
 *    fields ARE the payload, so the page just stores + dispatches them.)
 *
 * The message source IS gated to an <iframe> in this document (the GAM
 * SafeFrame): otherwise any script on the page could spoof the message and
 * inject an arbitrary clickable tile (phishing/brand harm). The click URL is
 * additionally scheme-validated (http/https only) so it can't smuggle a
 * `javascript:`/`data:` href, and a card only renders when a matching
 * NativeAdCard is mounted for that div id.
 *
 * Everything fails soft: missing/blank fields → no card (tile stays hidden);
 * an unmounted div id → the fields are cached and delivered if/when a card for
 * that id registers.
 */

// The native fields the GAM native style must emit. Mirrors GAM's standard
// native ad format fields (googletag native asset keys). Only clickUrl is
// strictly required to render a clickable card; headline OR image is required
// for the card to be worth showing.
export type NativeAdFields = {
  // GAM click-through URL WITH click macros expanded — the whole card is an
  // anchor to this, so GAM click tracking fires on the natural navigation.
  clickUrl: string;
  // Main native image asset (Image / MainImage).
  mainImage?: string;
  // Headline / Title.
  headline?: string;
  // Advertiser / brand name (Advertiser or SourceName).
  advertiser?: string;
  // Body / description (optional).
  body?: string;
  // Call-to-action text (optional, e.g. "Learn More").
  callToAction?: string;
};

export const NATIVE_AD_FIELDS_MESSAGE_TYPE = 'native-ad-fields';
export const NATIVE_AD_RENDER_HOOK_NAME = '__flipboardRenderNativeAd';

type NativeAdCallback = (fields: NativeAdFields) => void;

type RenderMessageEventLike = {
  data?: unknown;
  source?: unknown;
};

type IframeLike = { contentWindow?: unknown };

type RendererWindow = {
  [NATIVE_AD_RENDER_HOOK_NAME]?: (divId: string, fields: unknown) => boolean;
  addEventListener?: (
    type: string,
    listener: (event: RenderMessageEventLike) => void,
  ) => void;
  document?: {
    querySelectorAll?: (selector: string) => ArrayLike<IframeLike>;
  } | null;
};

const getRendererWindow = (): RendererWindow =>
  getWindow() as unknown as RendererWindow;

// React NativeAdCards awaiting native fields, keyed by div id.
const callbacks = new Map<string, NativeAdCallback>();
// Fields that arrived before a card registered (race: the friendly-iframe
// creative can fire the same tick the slot displays). Small FIFO cap.
const MAX_PENDING = 20;
const pendingFields = new Map<string, NativeAdFields>();

/** Test-only reset (module state would otherwise leak across tests). */
export const clearNativeAdState = (): void => {
  callbacks.clear();
  pendingFields.clear();
};

// A non-blank string, else undefined.
function nonEmptyString(v: unknown): string | undefined {
  if (typeof v === 'string' && v.trim() !== '') {
    return v;
  }
  return undefined;
}

// A safe navigable URL (http/https only). The click URL becomes the whole
// tile's <a href>, so a crafted `javascript:`/`data:`/`vbscript:` value could
// execute script or navigate unsafely — reject anything but http(s).
function safeHttpUrl(v: unknown): string | undefined {
  const s = nonEmptyString(v);
  if (!s) {
    return undefined;
  }
  try {
    const base = (getWindow() as { location?: { href?: string } } | null)
      ?.location?.href;
    const parsed = new URL(s, base || undefined);
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      return parsed.href;
    }
  } catch (_) {
    // Unparseable → reject.
  }
  return undefined;
}

// A safe image src (http/https/protocol-relative). Rejects data:/javascript:
// so a hostile payload can't smuggle script/markup through the <img src>.
function safeImageUrl(v: unknown): string | undefined {
  return safeHttpUrl(v);
}

// Coerce + validate the posted/handed fields. Returns null when there is
// nothing worth rendering (fail-soft: the card stays hidden).
const normalizeFields = (raw: unknown): NativeAdFields | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const f = raw as Record<string, unknown>;
  const clickUrl = safeHttpUrl(f.clickUrl);
  // A native ad with no (safe) click-through is not renderable as a card.
  if (!clickUrl) {
    return null;
  }
  const headline = nonEmptyString(f.headline);
  const mainImage = safeImageUrl(f.mainImage);
  // Need at least a headline or an image or the tile is an empty box.
  if (!headline && !mainImage) {
    return null;
  }
  return {
    clickUrl,
    mainImage,
    headline,
    advertiser: nonEmptyString(f.advertiser),
    body: nonEmptyString(f.body),
    callToAction: nonEmptyString(f.callToAction),
  };
};

const stashPending = (divId: string, fields: NativeAdFields): void => {
  pendingFields.delete(divId);
  pendingFields.set(divId, fields);
  while (pendingFields.size > MAX_PENDING) {
    const oldest = pendingFields.keys().next().value;
    if (oldest === undefined) {
      break;
    }
    pendingFields.delete(oldest);
  }
};

/**
 * Deliver native fields for a div id: hand them to a registered card, or stash
 * them until one registers. Returns true when the fields were valid (rendered
 * or stashed), false on a fail-soft no-op (nothing worth showing).
 */
export const deliverNativeAdFields = (divId: string, raw: unknown): boolean => {
  const fields = normalizeFields(raw);
  if (!divId || !fields) {
    return false;
  }
  const callback = callbacks.get(divId);
  if (callback) {
    callback(fields);
  } else {
    stashPending(divId, fields);
  }
  return true;
};

/**
 * Friendly-iframe render path, exposed on the page as
 * window.__flipboardRenderNativeAd(divId, fields). Called by the GAM native
 * style with its slot's div id and native field bundle.
 */
export const renderNativeAd = (divId: string, fields: unknown): boolean =>
  deliverNativeAdFields(String(divId), fields);

/**
 * SafeFrame fallback: the creative cannot reach `parent`, so it posts
 * {type: 'native-ad-fields', divId, fields}. The page reads the fields off the
 * message and delivers them (no reply needed — the fields are the payload).
 */
export const handleNativeAdMessage = (event: RenderMessageEventLike): void => {
  const data = event?.data as {
    type?: unknown;
    divId?: unknown;
    fields?: unknown;
  } | null;
  if (
    !data ||
    data.type !== NATIVE_AD_FIELDS_MESSAGE_TYPE ||
    (typeof data.divId !== 'string' && typeof data.divId !== 'number')
  ) {
    return;
  }
  // Only accept fields from an iframe that actually lives in this document
  // (the GAM SafeFrame). Comparing window references is allowed cross-origin;
  // this stops any other script on the page from spoofing the message to
  // inject an arbitrary clickable tile into the grid (phishing/brand harm).
  if (!isMessageFromOwnIframe(event.source)) {
    return;
  }
  deliverNativeAdFields(String(data.divId), data.fields);
};

// True when `source` is the contentWindow of an <iframe> in this document.
const isMessageFromOwnIframe = (source: unknown): boolean => {
  if (!source) {
    return false;
  }
  const doc = getRendererWindow().document;
  if (!doc || typeof doc.querySelectorAll !== 'function') {
    return false;
  }
  const iframes = doc.querySelectorAll('iframe');
  for (let i = 0; i < iframes.length; i++) {
    if (iframes[i].contentWindow === source) {
      return true;
    }
  }
  return false;
};

/**
 * Install the page-side receiver hooks (idempotent — keyed off the window so
 * it re-installs on a fresh window in tests). Called when a NativeAdCard
 * registers, i.e. client-side only.
 */
export const installNativeAdRenderer = (): void => {
  const win = getRendererWindow();
  if (!win || win[NATIVE_AD_RENDER_HOOK_NAME]) {
    return;
  }
  win[NATIVE_AD_RENDER_HOOK_NAME] = renderNativeAd;
  if (typeof win.addEventListener === 'function') {
    win.addEventListener('message', handleNativeAdMessage);
  }
};

/**
 * Register a React NativeAdCard's callback for a div id. Installs the page
 * hooks (idempotent) and immediately flushes any fields that arrived before
 * the card mounted. Returns an unregister fn to call on unmount / reset.
 */
export const registerNativeAdCallback = (
  divId: string,
  callback: NativeAdCallback,
): (() => void) => {
  installNativeAdRenderer();
  callbacks.set(divId, callback);
  const pending = pendingFields.get(divId);
  if (pending) {
    pendingFields.delete(divId);
    callback(pending);
  }
  return () => {
    if (callbacks.get(divId) === callback) {
      callbacks.delete(divId);
    }
    pendingFields.delete(divId);
  };
};
